import React from "react";
import { useState, useEffect } from "react";
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Box, Paper, Typography, TextField, CircularProgress } from "@mui/material";
import { Toolbar } from "@mui/material";
import { IconButton } from "@mui/material";
import { Button } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { List } from "@mui/material";
import { Tooltip } from "@mui/material";
import {
  ApiGet,
  ApiPost,
  ApiPut,
  ApiDelete,
} from "../helpers/API/ApiData";
import DeleteIcon from "@mui/icons-material/Delete";
import CreateIcon from "@mui/icons-material/Create";
import { toast, ToastContainer } from "react-toastify";
import moment from "moment";
import { usePermissions } from "../hooks/usePermissions";
import { useImageCrop, useContentLanguage } from "../hooks";
import { DataTable, TableColumnDef } from "../components/DataTable";
import { PageHeader } from "../components/PageHeader";
import { TableThumbnail } from "../components/TableThumbnail";
import { ImageCropField } from "../components/ImageCropField";
import { LanguageTabs } from "../components/LanguageTabs";
import { sanitizeInput } from "../utils/sanitize";

export default function BulletinPage() {
  const { canCreate, canEdit, canDelete } = usePermissions("/post");
  const [bulletinData, setBulletinData] = useState<any>({});
  const [addBulletin, setAddBulletin] = useState(false);
  const [errors, setErrors] = useState<any>({});
  const [getAllBulletin, setGetAllBulletin] = useState<any[]>([]);
  const [isEditApi, setIsEditApi] = useState(false);
  const [bulletinIdForDelete, setBulletinIdForDelete] = useState<any>();
  const [bulletinIdForEdit, setBulletinIdForEdit] = useState<any>();
  const [countPerPage, setCountPerPage] = useState(10);
  const [loader, setLoader] = useState(false);
  const [loaderForGetAll, setLoaderForGetAll] = useState(false);
  const [show, setShow] = useState(false);
  const [search, setSearch] = useState<any>();
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);

  const imageCrop = useImageCrop();
  const [removeBulletinImage, setRemoveBulletinImage] = useState(false);
  const [lang, setLang] = useContentLanguage();

  useEffect(() => {
    getAllBulletinData();
  }, [page, countPerPage, search]);

  const addBulletinModelClose = () => {
    setAddBulletin(false);
    setIsEditApi(false);
    setErrors({});
    setBulletinData({});
    imageCrop.reset();
    setRemoveBulletinImage(false);
  };

  const onhandleChange = (e: any) => {
    const { name, value } = e.target;
    setBulletinData({ ...bulletinData, [name]: sanitizeInput(value) });
    setErrors({ ...errors, [name]: "" });
  };

  const handleClose = () => {
    setShow(false);
  };

  const getAllBulletinData = async () => {
    setLoaderForGetAll(true);
    const params = `page=${page}&limit=${countPerPage}${search ? `&letter=${search}` : ""}`;
    await ApiGet(`post/find?${params}`)
      .then((res: any) => {
        setGetAllBulletin(res?.data?.data);
        setCount(res?.data?.count ?? 0);
        setLoaderForGetAll(false);
      })
      .catch(() => {
        setLoaderForGetAll(false);
      });
  };

  const formValidation = () => {
    let formErrors: any = {};
    let isValid = true;
    if (!bulletinData?.headline_ger) {
      formErrors.headline_ger = "Schlagzeile (DE) ist erforderlich";
      isValid = false;
    }
    if (!bulletinData?.headline) {
      formErrors.headline = "Schlagzeile (EN) ist erforderlich";
      isValid = false;
    }
    if (!bulletinData?.description_ger) {
      formErrors.description_ger = "Beschreibung (DE) ist erforderlich";
      isValid = false;
    }
    if (!bulletinData?.description) {
      formErrors.description = "Beschreibung (EN) ist erforderlich";
      isValid = false;
    }
    setErrors(formErrors);
    return isValid;
  };

  const addBulletinData = async () => {
    if (!formValidation()) return;
    setIsEditApi(false);
    setLoader(true);
    try {
      const formData = new FormData();
      formData.append("headline", bulletinData?.headline);
      formData.append("headline_ger", bulletinData?.headline_ger);
      formData.append("description", bulletinData?.description);
      formData.append("description_ger", bulletinData?.description_ger);

      const croppedFile = await imageCrop.getCroppedFile();
      if (croppedFile) formData.append("file", croppedFile);

      await ApiPost(`post/create`, formData);
      toast.success("Bulletin erfolgreich hinzugefügt");
      setAddBulletin(false);
      setIsEditApi(false);
      getAllBulletinData();
      setBulletinData({});
      imageCrop.reset();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Etwas ist schief gelaufen...");
    } finally {
      setLoader(false);
    }
  };

  const editBulletinData = async () => {
    if (!formValidation()) return;
    setLoader(true);
    try {
      const formData = new FormData();
      formData.append("headline", bulletinData?.headline);
      formData.append("headline_ger", bulletinData?.headline_ger);
      formData.append("description", bulletinData?.description);
      formData.append("description_ger", bulletinData?.description_ger);

      const croppedFile = await imageCrop.getCroppedFile();
      if (croppedFile) formData.append("file", croppedFile);
      if (removeBulletinImage) formData.append("removeImages", "true");

      await ApiPut(`post/update?id=${bulletinIdForEdit}`, formData);
      toast.success("Bulletin Update erfolgreich");
      getAllBulletinData();
      setAddBulletin(false);
      setIsEditApi(false);
      setBulletinData({});
      imageCrop.reset();
      setRemoveBulletinImage(false);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Etwas ist schief gelaufen...");
    } finally {
      setLoader(false);
    }
  };

  const deleteBulletinData = async () => {
    await ApiDelete(`post/delete?id=${bulletinIdForDelete}`)
      .then((res: any) => {
        if (res?.status === 200) {
          toast.success("Bulletin erfolgreich gelöscht");
          setShow(false);
          setPage(1);
          getAllBulletinData();
        } else {
          toast.error(res?.data?.message || "Etwas ist schief gelaufen...");
        }
      })
      .catch((err: any) => {
        toast.error(err?.response?.data?.message || "Etwas ist schief gelaufen...");
      });
  };

  const columns: TableColumnDef[] = [
    {
      header: "Erstellt am",
      render: (row: any) => moment(row?.createdAt).format("DD.MM.YYYY"),
      getValue: (row: any) => row?.createdAt,
    },
    {
      header: "Schlagzeilen",
      render: (row: any) => (lang === "de" ? row?.headline_ger : row?.headline) || "-",
      getValue: (row: any) => lang === "de" ? row?.headline_ger : row?.headline,
    },
    {
      header: "Beschreibung",
      render: (row: any) => (lang === "de" ? row?.description_ger : row?.description) || "-",
      getValue: (row: any) => lang === "de" ? row?.description_ger : row?.description,
    },
    {
      header: "Bilder",
      render: (row: any) => (
        <TableThumbnail src={row?.images} />
      ),
    },
    {
      header: "Einstellungen",
      render: (row: any) => (
        <Box sx={{ display: "flex", gap: 0.5 }}>
          {canEdit && (
            <Tooltip title="Bearbeiten" arrow>
              <IconButton
                size="small"
                color="primary"
                onClick={() => {
                  setIsEditApi(true);
                  setAddBulletin(true);
                  setBulletinIdForEdit(row?._id);
                  setBulletinData({
                    headline: row?.headline,
                    headline_ger: row?.headline_ger,
                    description: row?.description,
                    description_ger: row?.description_ger,
                    image: row?.images,
                  });
                }}
              >
                <CreateIcon />
              </IconButton>
            </Tooltip>
          )}
          {canDelete && (
            <Tooltip title="Löschen" arrow>
              <IconButton
                size="small"
                color="error"
                onClick={() => {
                  setBulletinIdForDelete(row?._id);
                  setShow(true);
                }}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  return (
    <>
      <Paper sx={{ p: 1 }}>
        <ToastContainer />
        <PageHeader
          title="Schwarzes Brett"
          onSearch={(val) => { setSearch(val); setPage(1); }}
          onAdd={canCreate ? () => {
            setIsEditApi(false);
            setBulletinData({});
            setErrors({});
            imageCrop.reset();
            setAddBulletin(true);
          } : undefined}
        />
        <LanguageTabs value={lang} onChange={setLang} />
        {loaderForGetAll ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2.5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <DataTable
            rows={getAllBulletin || []}
            columns={columns}
            page={page}
            count={count}
            rowsPerPage={countPerPage}
            onPageChange={setPage}
            onRowsPerPageChange={setCountPerPage}
          />
        )}
      </Paper>

      <Dialog open={show} onClose={handleClose}>
        <DialogTitle sx={{ color: "error.main", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          Löschen bestätigen
          <IconButton size="small" onClick={handleClose} aria-label="close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Sind Sie sicher, dass Sie dieses Bulletin löschen möchten?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="inherit">
            Nein
          </Button>
          <Button onClick={() => deleteBulletinData()} color="error" variant="contained">
            Ja
          </Button>
        </DialogActions>
      </Dialog>

      {addBulletin && (
        <Dialog fullScreen open={addBulletin}>
          <Toolbar>
            <IconButton
              edge="end"
              color="inherit"
              onClick={addBulletinModelClose}
              aria-label="close"
              sx={{ ml: "auto" }}
            >
              <CloseIcon />
            </IconButton>
          </Toolbar>
          <List>
            <Box sx={{ maxWidth: 1200, mx: "auto", p: 3 }}>
              {/* Language headers */}
              <Box sx={{ display: "flex", mb: 3, flexWrap: "wrap" }}>
                <Box sx={{ width: { xs: "100%", lg: "16.66%" } }} />
                <Box sx={{ width: { xs: "100%", lg: "41.67%" }, textAlign: "center" }}>
                  <Typography variant="subtitle1" fontWeight={600}>Deutsch</Typography>
                </Box>
                <Box sx={{ width: { xs: "100%", lg: "41.67%" }, textAlign: "center" }}>
                  <Typography variant="subtitle1" fontWeight={600}>English</Typography>
                </Box>
              </Box>

              {/* Schlagzeilen */}
              <Box sx={{ display: "flex", mb: 3, flexWrap: "wrap" }}>
                <Typography sx={{ width: { xs: "100%", lg: "16.66%" }, pt: 1 }}>
                  Schlagzeilen
                </Typography>
                <Box sx={{ width: { xs: "100%", lg: "41.67%" }, pr: 1 }}>
                  <TextField
                    size="small"
                    fullWidth
                    variant="outlined"
                    name="headline_ger"
                    value={bulletinData?.headline_ger || ""}
                    onChange={onhandleChange}
                  />
                  <Typography variant="caption" color="error">{errors?.headline_ger}</Typography>
                </Box>
                <Box sx={{ width: { xs: "100%", lg: "41.67%" }, pl: 1 }}>
                  <TextField
                    size="small"
                    fullWidth
                    variant="outlined"
                    name="headline"
                    value={bulletinData?.headline || ""}
                    onChange={onhandleChange}
                  />
                  <Typography variant="caption" color="error">{errors?.headline}</Typography>
                </Box>
              </Box>

              {/* Beschreibung */}
              <Box sx={{ display: "flex", mb: 3, flexWrap: "wrap" }}>
                <Typography sx={{ width: { xs: "100%", lg: "16.66%" }, pt: 1 }}>
                  Beschreibung
                </Typography>
                <Box sx={{ width: { xs: "100%", lg: "41.67%" }, pr: 1 }}>
                  <TextField
                    size="small"
                    fullWidth
                    variant="outlined"
                    name="description_ger"
                    value={bulletinData?.description_ger || ""}
                    onChange={onhandleChange}
                    multiline
                    rows={3}
                  />
                  <Typography variant="caption" color="error">{errors?.description_ger}</Typography>
                </Box>
                <Box sx={{ width: { xs: "100%", lg: "41.67%" }, pl: 1 }}>
                  <TextField
                    size="small"
                    fullWidth
                    variant="outlined"
                    name="description"
                    value={bulletinData?.description || ""}
                    onChange={onhandleChange}
                    multiline
                    rows={3}
                  />
                  <Typography variant="caption" color="error">{errors?.description}</Typography>
                </Box>
              </Box>

              {/* Bild (shared) */}
              <Box sx={{ display: "flex", mb: 3, flexWrap: "wrap" }}>
                <Typography sx={{ width: { xs: "100%", lg: "16.66%" }, pt: 1 }}>Bild</Typography>
                <Box sx={{ width: { xs: "100%", lg: "41.67%" } }}>
                  <ImageCropField
                    inputId="imageUpload"
                    imageCrop={imageCrop}
                    existingImage={bulletinData?.image}
                    error={errors["file"]}
                    onRemoveExistingImage={() => {
                      setBulletinData({ ...bulletinData, image: "" });
                      setRemoveBulletinImage(true);
                    }}
                  />
                </Box>
              </Box>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "center" }}>
              {loader ? (
                <Button variant="contained" type="button" disabled>
                  {isEditApi ? "Speichern" : "Hinzufügen"}
                  <CircularProgress size={20} sx={{ mx: 3, color: "white" }} />
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={() => {
                    isEditApi ? editBulletinData() : addBulletinData();
                  }}
                >
                  {isEditApi ? "Speichern" : "Hinzufügen"}
                </Button>
              )}
            </Box>
          </List>
        </Dialog>
      )}
    </>
  );
}
